import { requestGetUserInfo } from "@state/auth/AuthEffects";
import {
  requestDeleteOfficePicture,
  requestUpdateOfficeScellementDefaultPriority,
  requestUpdateOfficeSignatureDefaultPriority,
} from "@state/offices/OfficesEffects";
import {
  requestGetOfficePictureDefault,
  requestGetOfficeScellementDefault,
} from "@state/picture/PictureEffects";
import { Picture } from "@type/core/offices/offices.types";
import configuration from "@utils/Config";
import ecmaLogo from "@/static/core/img/logo_ecma.png";
import { SignaturePicturePriorityEnum } from "@utils/enums/signature.picture.priority.enum";
import { blobToBase64, convertImageToBlob } from "@utils/helpers/base64-helper";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";
import { getToken } from "@utils/rest/RestClient";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

interface UseOfficeSignatureReturn {
  officeId?: string;
  pictures: Picture[];
  setPictures: Dispatch<SetStateAction<Picture[]>>;
  priority?: SignaturePicturePriorityEnum;
  setPriority: Dispatch<
    SetStateAction<SignaturePicturePriorityEnum | undefined>
  >;
  handleDelete: (signatureType: SignaturePicturePriorityEnum) => void;
  handleDefault: (
    scellementPriority: SignaturePicturePriorityEnum,
    rank?: number,
  ) => void;
  buildPictureOfficeUrl: (pictureDefault: Picture[] | undefined) => string;
}

const useOfficeSignature = (
  signatureType: "ScellementPicture" | "SignaturePicture",
  defaultPriority: SignaturePicturePriorityEnum | undefined,
  pictureDefault: Picture[] | undefined,
  officeId?: string,
): UseOfficeSignatureReturn => {
  const { t } = useTranslation();

  const [pictures, setPictures] = useState<Picture[]>([]);
  const [priority, setPriority] = useState<SignaturePicturePriorityEnum>();

  const initialisePicture = useCallback(() => {
    const url = buildPictureOfficeUrl(pictureDefault);
    if (url === "") {
      convertImageToBlob(ecmaLogo)
        .then((blob) => {
          blobToBase64(blob)
            .then((base64Data) => {
              if (base64Data) {
                let defaultValue = false;
                if (
                  defaultPriority !== SignaturePicturePriorityEnum.NO_PICTURE
                ) {
                  defaultValue = true;
                }
                const defaultPicture: Picture[] = [
                  {
                    picture: base64Data,
                    rank: 1,
                    isDefault: defaultValue,
                    isDefaultImage: true,
                  },
                ];
                setPictures(defaultPicture);
              }
            })
            .catch(() => {
              toastError(t("offices.preferences.errors.blobToBase64"));
            });
        })
        .catch(() => {
          toastError(t("offices.preferences.errors.imgToBlob"));
        });
    } else {
      if (signatureType === "ScellementPicture") {
        void requestGetOfficeScellementDefault(url);
      } else {
        void requestGetOfficePictureDefault(url);
      }
    }
    setPriority(defaultPriority);
  }, [pictureDefault, signatureType, t]);

  useEffect(() => {
    initialisePicture();
  }, [initialisePicture]);

  useEffect(() => {
    if (signatureType === "SignaturePicture") {
      requestGetOfficePictureDefault.done.watch(({ result }) => {
        if (!result.ok && !result.data) {
          toastError(t("offices.preferences.errors.defaultPictureError"));
        } else {
          const blob = result.data;
          if (blob) {
            blobToBase64(blob)
              .then((base64Data) => {
                let defaultValue = false;
                if (
                  defaultPriority !== SignaturePicturePriorityEnum.NO_PICTURE
                ) {
                  defaultValue = true;
                }
                const picture: Picture[] = [
                  {
                    picture: base64Data,
                    rank: 1,
                    isDefault: defaultValue,
                  },
                ];
                setPictures(picture);
              })
              .catch(() => {
                toastError(t("offices.preferences.errors.blobToBase64"));
              });
          }
        }
      });
    }
    if (signatureType === "ScellementPicture") {
      requestGetOfficeScellementDefault.done.watch(({ result }) => {
        if (!result.ok && !result.data) {
          toastError(t("offices.preferences.errors.defaultPictureError"));
        } else {
          const blob = result.data;
          if (blob) {
            blobToBase64(blob)
              .then((base64Data) => {
                let defaultValue = false;
                if (
                  defaultPriority !== SignaturePicturePriorityEnum.NO_PICTURE
                ) {
                  defaultValue = true;
                }
                const picture: Picture[] = [
                  {
                    picture: base64Data,
                    rank: 1,
                    isDefault: defaultValue,
                  },
                ];
                setPictures(picture);
              })
              .catch(() => {
                toastError(t("offices.preferences.errors.blobToBase64"));
              });
          }
        }
      });
    }
  }, [t]);

  const handleDelete = () => {
    void requestDeleteOfficePicture({
      dto: {
        signaturePictureDefault:
          signatureType === "SignaturePicture" ? true : undefined,
        scellementPicture:
          signatureType === "ScellementPicture" ? true : undefined,
      },
      id: officeId,
    })
      .then(() => {
        void requestGetUserInfo({});
        toastSuccess(t("offices.preferences.errors.pictureDeleteSuccess"));
      })
      .catch(() => {
        toastError(t("offices.preferences.errors.pictureDeleteError"));
      });
  };

  const buildPictureOfficeUrl = (pictureDefault: Picture[] | undefined) => {
    if (pictureDefault && pictureDefault.length > 0) {
      return `${configuration.backendBaseUrl}${pictureDefault[0].picture}&token=${getToken()}`;
    } else {
      return "";
    }
  };

  const handleDefault = (
    signaturePriorityType: SignaturePicturePriorityEnum,
    rank?: number,
  ) => {
    if (!rank) {
      rank = 1;
    }
    if (signatureType === "ScellementPicture") {
      void requestUpdateOfficeScellementDefaultPriority({
        dto: {
          defaultSignature: signaturePriorityType,
        },
        id: officeId,
      })
        .then(() => {
          setPriority(signaturePriorityType);
          void requestGetUserInfo({});
          toastSuccess(t("offices.preferences.errors.updatePrioritySuccess"));
        })
        .catch(() => {
          toastError(t("offices.preferences.errors.updatePriorityError"));
        });
    } else {
      void requestUpdateOfficeSignatureDefaultPriority({
        dto: {
          defaultSignature: signaturePriorityType,
        },
        id: officeId,
      })
        .then(() => {
          setPriority(signaturePriorityType);
          void requestGetUserInfo({});
          toastSuccess(t("offices.preferences.errors.updatePrioritySuccess"));
        })
        .catch(() => {
          toastError(t("offices.preferences.errors.updatePriorityError"));
        });
    }
  };

  return {
    officeId,
    pictures,
    priority,
    setPictures,
    setPriority,
    handleDelete,
    handleDefault,
    buildPictureOfficeUrl,
  };
};

export default useOfficeSignature;
